import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  getPerson,
  getMissionsForPerson,
  getWeightEntriesForPerson,
  createWeightEntry,
} from "../../services/personService";
import PersonEditModal from "../../components/person/PersonEditModal";
import {
  Box,
  Paper,
  CircularProgress,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import { Edit as EditIcon } from "@mui/icons-material";

export default function PersonDetail({ id, isDashboard }) {
  const params = useParams();
  const personId = id || params.id;
  const navigate = useNavigate();

  console.log('PersonDetail mounted with personId:', personId, 'isDashboard:', isDashboard);

  const [person, setPerson] = useState(null);
  const [missions, setMissions] = useState([]);
  const [weightEntries, setWeightEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);

  const [openDialog, setOpenDialog] = useState(false);
  const [formData, setFormData] = useState({
    weighingDate: new Date().toISOString().split("T")[0],
    weightInKilograms: "",
    notes: "",
  });

  useEffect(() => {
    async function loadData() {
      try {
        console.log('Loading person data for personId:', personId);
        const personData = await getPerson(personId);
        console.log('Person data loaded:', personData);
        setPerson(personData);

        const missionsData = await getMissionsForPerson(personId);
        console.log('Missions data loaded:', missionsData);
        setMissions(missionsData);

        const weightEntriesData = await getWeightEntriesForPerson(personId);
        console.log('Weight entries loaded:', weightEntriesData);
        // Sort weight entries from newest to oldest
        const sorted = [...(weightEntriesData || [])].sort(
          (a, b) => new Date(b.weighingDate) - new Date(a.weighingDate)
        );
        setWeightEntries(sorted);
      } catch (err) {
        console.error("Error loading person detail:", err);
        setError("Fout bij het laden van gegevens: " + err.message);
      } finally {
        setLoading(false);
      }
    }

    if (personId) {
      loadData();
    }
  }, [personId]);

  const handleOpenDialog = () => {
    setFormData({
      weighingDate: new Date().toISOString().split("T")[0],
      weightInKilograms: "",
      notes: "",
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmitWeightEntry = async () => {
    try {
      if (!formData.weighingDate || !formData.weightInKilograms) {
        alert("Vul alstublieft alle verplichte velden in");
        return;
      }

      // Convert date from yyyy-mm-dd to dd/mm/yyyy
      const [year, month, day] = formData.weighingDate.split("-");
      const formattedDate = `${day}/${month}/${year}`;

      await createWeightEntry(personId, {
        weighingDate: formattedDate,
        weightInKilograms: parseFloat(formData.weightInKilograms),
        notes: formData.notes,
      });

      // Reload weight entries
      const updatedEntries = await getWeightEntriesForPerson(personId);
      const sorted = [...(updatedEntries || [])].sort(
        (a, b) => new Date(b.weighingDate) - new Date(a.weighingDate)
      );
      setWeightEntries(sorted);

      // Reload person data to update current weight
      const updatedPerson = await getPerson(personId);
      setPerson(updatedPerson);

      handleCloseDialog();
    } catch (err) {
      console.error("Error creating weight entry:", err);
      console.error("Response data:", err.response?.data);
      console.error("Validation errors:", err.response?.data?.errors);
      const validationErrors = err.response?.data?.errors;
      let errorMsg = "Fout bij het opslaan van gewichtsgegevens";

      if (validationErrors) {
        errorMsg = Object.values(validationErrors).flat().join(", ");
      } else {
        errorMsg = err.response?.data?.message || err.message || errorMsg;
      }

      alert(errorMsg);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 5 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!person) {
    return (
      <Alert severity="warning" sx={{ mt: 2 }}>
        Persoon niet gevonden
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h4" sx={{ mb: 3 }}>
        {person.firstName} {person.lastName}
      </Typography>

      {/* Gegevens Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            Gegevens
          </Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<EditIcon />}
            onClick={() => setEditModalOpen(true)}
          >
            Wijzigen
          </Button>
        </Box>
        <Box sx={{ display: "grid", gridTemplateColumns: "200px 1fr", gap: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: "bold" }}>
            Voornaam
          </Typography>
          <Typography variant="body2">{person.firstName}</Typography>

          <Typography variant="body2" sx={{ fontWeight: "bold" }}>
            Achternaam
          </Typography>
          <Typography variant="body2">{person.lastName}</Typography>

          <Typography variant="body2" sx={{ fontWeight: "bold" }}>
            Lengte
          </Typography>
          <Typography variant="body2">{person.heightInCentimeters} cm</Typography>

          <Typography variant="body2" sx={{ fontWeight: "bold" }}>
            Huidig gewicht
          </Typography>
          <Typography variant="body2">{person.currentWeight ?? "-"} kg</Typography>

          <Typography variant="body2" sx={{ fontWeight: "bold" }}>
            BMI
          </Typography>
          <Typography variant="body2">{person.bmi?.toFixed(2) ?? "-"}</Typography>

          <Typography variant="body2" sx={{ fontWeight: "bold" }}>
            Status
          </Typography>
          <Typography variant="body2">{person.status}</Typography>

          <Typography variant="body2" sx={{ fontWeight: "bold" }}>
            Gemaakt op
          </Typography>
          <Typography variant="body2">
            {new Date(person.createdAt).toLocaleDateString("nl-NL")}
          </Typography>

          <Typography variant="body2" sx={{ fontWeight: "bold" }}>
            Gewijzigd op
          </Typography>
          <Typography variant="body2">
            {new Date(person.updatedAt).toLocaleDateString("nl-NL")}
          </Typography>
        </Box>
      </Paper>

      {/* Missies Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Missies
        </Typography>
        {missions.length === 0 ? (
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Geen missies
          </Typography>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Naam</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Beschrijving</TableCell>
                <TableCell>Startdatum</TableCell>
                <TableCell>Doeldatum</TableCell>
                <TableCell>Voltooid %</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {missions.map((mission) => (
                <TableRow
                  key={mission.id}
                  onClick={() => navigate(`/missions/${mission.id}`)}
                  sx={{ cursor: "pointer", "&:hover": { backgroundColor: "#f5f5f5" } }}
                >
                  <TableCell>{mission.name}</TableCell>
                  <TableCell>{mission.type}</TableCell>
                  <TableCell>{mission.status}</TableCell>
                  <TableCell>{mission.description || "-"}</TableCell>
                  <TableCell>{mission.startDate}</TableCell>
                  <TableCell>{mission.targetDate}</TableCell>
                  <TableCell>
                    {mission.percentageCompleted?.toFixed(1) ?? "-"}%
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      {/* Gewicht Section */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Gewicht
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={handleOpenDialog}
          sx={{ mb: 2 }}
        >
          Geef nieuw gewicht in
        </Button>
        {weightEntries.length === 0 ? (
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Geen gewichtsgegevens
          </Typography>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Weegdatum</TableCell>
                <TableCell>Gewicht (kg)</TableCell>
                <TableCell>Notities</TableCell>
                <TableCell>Gemaakt op</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {weightEntries.map((entry) => (
                <TableRow key={entry.id}>
                  <TableCell>{entry.weighingDate}</TableCell>
                  <TableCell>{entry.weightInKilograms}</TableCell>
                  <TableCell>{entry.notes || "-"}</TableCell>
                  <TableCell>
                    {new Date(entry.createdAt).toLocaleDateString("nl-NL")}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      {/* Dialog for adding new weight entry */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Nieuw gewicht invoeren</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <TextField
            fullWidth
            label="Weegdatum"
            name="weighingDate"
            type="date"
            value={formData.weighingDate}
            onChange={handleFormChange}
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Gewicht (kg)"
            name="weightInKilograms"
            type="number"
            inputProps={{ step: "0.1" }}
            value={formData.weightInKilograms}
            onChange={handleFormChange}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Notities (optioneel)"
            name="notes"
            multiline
            rows={3}
            value={formData.notes}
            onChange={handleFormChange}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Annuleren</Button>
          <Button onClick={handleSubmitWeightEntry} variant="contained" color="primary">
            Opslaan
          </Button>
        </DialogActions>
      </Dialog>

      {/* Personal Edit Modal */}
      <PersonEditModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        person={person}
        onPersonUpdated={setPerson}
      />
    </Box>
  );
}
